import { router, useLocalSearchParams } from 'expo-router';
import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { ReportControls, type ReportControlsValue } from './report-controls';
import { ReportSummary } from './report-summary';

import { ActivityIndicator, Button, Text, useTheme } from '@/components/ui';
import { useExteriorImprovementsReport } from '@/hooks/use-exterior-improvements-report';
import type { ExteriorImprovementsReportParams } from '@/services/data-service';
import { exportRowsToCsv, exportRowsToXlsx } from '@/utils/accounting-export';
import { isValidIsoDay, todayBusinessIso } from '@/utils/date-utils';
import {
	EXTERIOR_REPORT_COLUMNS,
	exteriorImprovementsReportQuery,
	flattenReportRows,
	normalizeStatuses,
	presetRange,
	REPORT_PRESETS,
	type ReportPreset,
	statusesFromParam,
} from '@/utils/exterior-improvements-report';
import { openPrintRoute } from '@/utils/print-navigation';

type ReportUrlParams = {
	preset?: string;
	from?: string;
	to?: string;
	dateField?: string;
	status?: string;
	associationId?: string;
};

const DEFAULT_PRESET: ReportPreset = 'last-month';

const isPreset = (v: string | undefined): v is ReportPreset => REPORT_PRESETS.some((p) => p.key === v);

/**
 * URL params → controls value. A preset's range is always re-derived from ET today — a
 * bookmarked "last month" means last month NOW, not the month it was saved in — so only a
 * `custom` range reads from/to off the URL, falling back to the default preset's range.
 */
const controlsFromUrl = (p: ReportUrlParams): ReportControlsValue => {
	const preset: ReportPreset = isPreset(p.preset) ? p.preset : DEFAULT_PRESET;
	const today = todayBusinessIso();
	const presetDates = presetRange(preset === 'custom' ? DEFAULT_PRESET : preset) ?? { from: today, to: today };
	const custom = preset === 'custom';
	return {
		preset,
		// Same rule as the API's parser: an impossible day (2026-02-30) falls back to the default
		// range rather than reaching the server as a 400.
		from: custom && isValidIsoDay(p.from) ? p.from : presetDates.from,
		to: custom && isValidIsoDay(p.to) ? p.to : presetDates.to,
		dateField: p.dateField === 'decided' ? 'decided' : 'submitted',
		statuses: statusesFromParam(p.status),
		associationId: p.associationId || undefined,
	};
};

/**
 * Body of the Exterior Improvements report (#644), shared by the per-association page and the
 * portfolio page. Filters live in the URL so a report is bookmarkable; the controls stay
 * mounted while a refetch runs (CLAUDE.md "Stable Search Inputs").
 */
export const ExteriorImprovementsReportBody: React.FC<{
	associationId?: string;
	showAssociationPicker?: boolean;
}> = ({ associationId, showAssociationPicker = false }) => {
	const theme = useTheme();
	const url = useLocalSearchParams<ReportUrlParams>();
	const value = controlsFromUrl(url);

	const statusKey = value.statuses.join(',');
	const scopedAssociationId = associationId ?? value.associationId;
	const params = useMemo<ExteriorImprovementsReportParams>(() => ({
		from: value.from,
		to: value.to,
		dateField: value.dateField,
		statuses: statusKey ? statusKey.split(',').map(Number) : [],
		associationId: scopedAssociationId,
	}), [value.from, value.to, value.dateField, statusKey, scopedAssociationId]);

	const { data: report, isLoading, isFetching, isPlaceholderData, error } = useExteriorImprovementsReport(params);
	const rows = useMemo(() => (report ? flattenReportRows(report) : []), [report]);
	// While a new range loads, `report` is the PREVIOUS range's placeholder: exporting it would
	// write the old rows under the new range's filename, so every export waits for the real data.
	const canExport = !!report && !isPlaceholderData;

	const onChange = (next: ReportControlsValue) => {
		router.setParams({
			preset: next.preset,
			from: next.from,
			to: next.to,
			dateField: next.dateField,
			status: next.statuses.length ? normalizeStatuses(next.statuses).join(',') : undefined,
			// The per-association page's scope is its route id, never a URL filter.
			...(showAssociationPicker ? { associationId: next.associationId } : {}),
		});
	};

	const filename = `exterior-improvements-${params.from}-${params.to}`;
	const printUrl = (mode: 'summary' | 'full') =>
		`/print/exterior-improvements?${exteriorImprovementsReportQuery(params)}&mode=${mode}`;

	return (
		<View style={styles.container}>
			<ReportControls value={value} onChange={onChange} showAssociationPicker={showAssociationPicker} />

			<View style={styles.actions}>
				<Button
					mode="outlined"
					compact
					icon="download-outline"
					testID="export-csv"
					disabled={!canExport}
					onPress={() => void exportRowsToCsv(filename, EXTERIOR_REPORT_COLUMNS, rows)}
				>
					CSV
				</Button>
				<Button
					mode="outlined"
					compact
					icon="grid-outline"
					testID="export-xlsx"
					disabled={!canExport}
					onPress={() => void exportRowsToXlsx(filename, EXTERIOR_REPORT_COLUMNS, rows)}
				>
					Excel
				</Button>
				{Platform.OS === 'web' ? (
					<>
						<Button
							mode="outlined"
							compact
							icon="print-outline"
							testID="print-summary"
							disabled={!canExport}
							onPress={() => openPrintRoute(printUrl('summary'))}
						>
							Print summary
						</Button>
						<Button
							mode="outlined"
							compact
							icon="documents-outline"
							testID="print-full"
							disabled={!canExport}
							onPress={() => openPrintRoute(printUrl('full'))}
						>
							Print full packet
						</Button>
					</>
				) : null}
				{isFetching ? <ActivityIndicator size="small" /> : null}
			</View>

			{error ? (
				<Text style={{ color: theme.colors.error }}>
					{error instanceof Error ? error.message : 'Could not load the report.'}
				</Text>
			) : null}
			{isLoading && !report ? <ActivityIndicator size="large" /> : null}
			{report ? <ReportSummary report={report} /> : null}
		</View>
	);
};

const styles = StyleSheet.create({
	container: { gap: 8 },
	actions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 },
});
